import { useEffect, useRef, useState } from "react";
import type { MakingStep } from "@ai-rpg/protocol";
import { getMaking } from "../api.ts";

/** What each step of making a game is, said to whoever is waiting on it. */
export const MAKING_WORDS: Record<MakingStep, string> = {
  preparing: "Getting your character ready",
  world: "Writing the world: its places, its people and what is wrong there",
  outline: "Reading the book for the first time",
  stage: "Writing out the adventure's first part",
  maps: "Reading the book's maps",
  companions: "Finding who travels with you",
  setting: "Setting the scene",
};

export const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * A game being made, for whoever waits on it: what the server is doing just now, as it says, and how long it
 * has been. A wait this long is told a step at a time, not left behind one unmoving line.
 */
export function Making({ usual }: { readonly usual: string }) {
  const [step, setStep] = useState<MakingStep>("preparing");
  const [seconds, setSeconds] = useState(0);
  const here = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // It is under the button that began it, which may be at the foot of the window: brought into view.
    const shown = window.requestAnimationFrame(() => here.current?.scrollIntoView({ block: "center" }));
    const began = Date.now();
    const tick = window.setInterval(() => setSeconds(Math.round((Date.now() - began) / 1000)), 1000);
    const ask = window.setInterval(() => {
      getMaking()
        .then((m) => m && setStep(m.step))
        .catch(() => undefined);
    }, 2000);
    // Leaving now would lose sight of it: the browser asks first.
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => {
      window.cancelAnimationFrame(shown);
      window.clearInterval(tick);
      window.clearInterval(ask);
      window.removeEventListener("beforeunload", warn);
    };
  }, []);
  return (
    <div className="making" ref={here}>
      <p className="making-step" role="status">
        {MAKING_WORDS[step]}…
      </p>
      <p className="field-note">
        <span className="figures" aria-hidden="true">
          {clock(seconds)}
        </span>{" "}
        {usual} Keep this page open: the game opens by itself when it is ready.
      </p>
    </div>
  );
}
